<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3'
import { LogOut, Menu, Monitor, Moon, Plus, Sun, X } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'

import AppLogoIcon from '@/components/AppLogoIcon.vue'
import InstallBanner from '@/components/InstallBanner.vue'
import NotificationBell from '@/components/NotificationBell.vue'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useAppearance } from '@/composables/useAppearance'
import {
  applyPagePath,
  newPortalOrganizationApplicationPath,
  newPortalPartnerApplicationPath,
  pollPortalCustomerNotificationsPath,
  pollPortalOrganizationNotificationsPath,
  pollPortalPartnerNotificationsPath,
  portalCustomerApplicationsPath,
  portalCustomerNotificationPath,
  portalCustomerNotificationsPath,
  portalCustomerPushSubscriptionsPath,
  portalCustomerRootPath,
  portalOrganizationApplicationsPath,
  portalOrganizationNotificationPath,
  portalOrganizationNotificationsPath,
  portalOrganizationPushSubscriptionsPath,
  portalOrganizationRootPath,
  portalOrganizationSettingsPath,
  portalPartnerNotificationPath,
  portalPartnerNotificationsPath,
  portalPartnerPushSubscriptionsPath,
  portalPartnerRootPath,
  portalPartnerSettingsPath,
  portalSessionPath,
  readAllPortalCustomerNotificationsPath,
  readAllPortalOrganizationNotificationsPath,
  readAllPortalPartnerNotificationsPath,
  readPortalCustomerNotificationPath,
  readPortalOrganizationNotificationPath,
  readPortalPartnerNotificationPath,
} from '@/routes'
import type { Auth } from '@/types'

interface NavItem {
  label: string
  href: string
  external?: boolean
  primary?: boolean
}

const props = defineProps<{
  actorType: 'organization' | 'partner' | 'customer'
}>()

const page = usePage()
const mobileMenuOpen = ref(false)
const { appearance, updateAppearance } = useAppearance()

// Register the service worker on every portal page so the PWA is always
// installable, regardless of whether push notifications are enabled.
onMounted(() => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/service-worker').catch(() => {})
  }
})

// Only partner/organization staff create applications on a customer's behalf —
// customers apply for themselves via the "Apply" link below, so they don't get
// a "New Application" nav item.
const navItems = computed<NavItem[]>(() => {
  if (props.actorType === 'organization') {
    return [
      { label: 'Dashboard', href: portalOrganizationRootPath() },
      { label: 'Applications', href: portalOrganizationApplicationsPath() },
      { label: 'Settings', href: portalOrganizationSettingsPath() },
      { label: 'New Application', href: newPortalOrganizationApplicationPath(), primary: true },
    ]
  }
  if (props.actorType === 'partner') {
    return [
      { label: 'Dashboard', href: portalPartnerRootPath() },
      { label: 'Settings', href: portalPartnerSettingsPath() },
      { label: 'New Application', href: newPortalPartnerApplicationPath(), primary: true },
    ]
  }
  return [
    { label: 'Dashboard', href: portalCustomerRootPath() },
    { label: 'Applications', href: portalCustomerApplicationsPath() },
    { label: 'Apply', href: applyPagePath({ customer_id: (page.props.auth as Auth).customer?.id }), external: true },
  ]
})

const actorName = computed<string>(() => {
  const auth = page.props.auth as Auth
  if (props.actorType === 'partner') return auth.partner?.name ?? ''
  if (props.actorType === 'organization') return auth.organization?.name ?? ''
  if (props.actorType === 'customer') {
    const c = auth.customer
    return c ? `${c.first_name} ${c.last_name}` : ''
  }
  return ''
})

const notificationProps = computed(() => {
  if (props.actorType === 'partner') {
    return {
      notificationsPath: portalPartnerNotificationsPath(),
      readPath: (id: number) => readPortalPartnerNotificationPath(id),
      readAllPath: readAllPortalPartnerNotificationsPath(),
      pollPath: pollPortalPartnerNotificationsPath(),
      deleteNotificationPath: (id: number) => portalPartnerNotificationPath(id),
    }
  }
  if (props.actorType === 'organization') {
    return {
      notificationsPath: portalOrganizationNotificationsPath(),
      readPath: (id: number) => readPortalOrganizationNotificationPath(id),
      readAllPath: readAllPortalOrganizationNotificationsPath(),
      pollPath: pollPortalOrganizationNotificationsPath(),
      deleteNotificationPath: (id: number) => portalOrganizationNotificationPath(id),
    }
  }
  if (props.actorType === 'customer') {
    return {
      notificationsPath: portalCustomerNotificationsPath(),
      readPath: (id: number) => readPortalCustomerNotificationPath(id),
      readAllPath: readAllPortalCustomerNotificationsPath(),
      pollPath: pollPortalCustomerNotificationsPath(),
      deleteNotificationPath: (id: number) => portalCustomerNotificationPath(id),
    }
  }
  return null
})

const pushSubscribePath = computed(() => {
  if (props.actorType === 'partner') return portalPartnerPushSubscriptionsPath()
  if (props.actorType === 'organization') return portalOrganizationPushSubscriptionsPath()
  if (props.actorType === 'customer') return portalCustomerPushSubscriptionsPath()
  return undefined
})

const themeIcon = computed(() => {
  if (appearance.value === 'dark') return Moon
  if (appearance.value === 'light') return Sun
  return Monitor
})

function cycleTheme() {
  const next = { system: 'light', light: 'dark', dark: 'system' } as const
  updateAppearance(next[appearance.value])
}

function signOut() {
  router.delete(portalSessionPath())
}

function isActive(href: string): boolean {
  if (page.url === href) return true
  if (!page.url.startsWith(href + '/')) return false
  // Don't mark as active if a more specific nav item also matches (exact or prefix)
  return !navItems.value.some(
    (item) => item.href !== href && (page.url === item.href || page.url.startsWith(item.href + '/')),
  )
}
</script>

<template>
  <div class="bg-background min-h-svh">
    <!-- Top nav -->
    <header class="border-border bg-background sticky top-0 z-50 border-b">
      <div class="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
        <div class="flex items-center gap-2">
          <AppLogoIcon class="text-foreground size-12 fill-current" />
          <Separator orientation="vertical" class="h-5" />
          <span class="text-muted-foreground text-sm">{{ actorName }}</span>
        </div>

        <!-- Desktop nav -->
        <nav class="hidden items-center gap-1 md:flex">
          <Button
            v-for="item in navItems"
            :key="item.href"
            :variant="item.primary ? 'default' : 'ghost'"
            size="sm"
            :class="[
              'text-sm',
              item.primary ? '' : isActive(item.href) ? 'bg-muted font-medium' : 'text-muted-foreground',
            ]"
            as-child
          >
            <a v-if="item.external" :href="item.href">{{ item.label }}</a>
            <Link v-else :href="item.href">
              <Plus v-if="item.primary" class="size-4" />
              {{ item.label }}
            </Link>
          </Button>
        </nav>

        <div class="flex items-center gap-1">
          <Button variant="ghost" size="icon" class="size-8" @click="cycleTheme">
            <component :is="themeIcon" class="size-4" />
            <span class="sr-only">Toggle theme</span>
          </Button>
          <NotificationBell
            v-if="notificationProps"
            v-bind="notificationProps"
            :push-subscribe-path="pushSubscribePath"
            :push-unsubscribe-path="pushSubscribePath"
            :auto-subscribe-push="actorType === 'customer'"
          />
          <Button variant="ghost" size="sm" class="text-muted-foreground hidden md:flex" @click="signOut">
            <LogOut class="size-4" />
            Sign out
          </Button>
          <Button variant="ghost" size="icon" class="md:hidden" @click="mobileMenuOpen = !mobileMenuOpen">
            <Menu v-if="!mobileMenuOpen" class="size-5" />
            <X v-else class="size-5" />
          </Button>
        </div>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="border-border border-t md:hidden">
        <nav class="flex flex-col gap-1 px-4 py-3">
          <Button
            v-for="item in navItems"
            :key="item.href"
            :variant="item.primary ? 'default' : 'ghost'"
            size="sm"
            :class="[
              'justify-start text-sm',
              item.primary ? '' : isActive(item.href) ? 'bg-muted font-medium' : 'text-muted-foreground',
            ]"
            as-child
          >
            <a v-if="item.external" :href="item.href" @click="mobileMenuOpen = false">{{ item.label }}</a>
            <Link v-else :href="item.href" @click="mobileMenuOpen = false">
              <Plus v-if="item.primary" class="size-4" />
              {{ item.label }}
            </Link>
          </Button>
          <Button variant="ghost" size="sm" class="text-muted-foreground justify-start" @click="signOut">
            <LogOut class="size-4" />
            Sign out
          </Button>
        </nav>
      </div>
    </header>

    <!-- Page content -->
    <main class="mx-auto max-w-5xl px-4 py-6">
      <slot />
    </main>
  </div>
  <InstallBanner />
</template>
